import React, { Fragment, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

/* ------------------------------------------------------------------ */
/*  Hero band for the About Us pages (jjfv2 design system).            */
/*  Behind the eyebrow/title/breadcrumb sits a small <canvas> with a    */
/*  few soft bubbles that slowly rise (bottom -> top) and respawn — a    */
/*  very simple, gentle animation confined to the hero. It is JS-driven  */
/*  (requestAnimationFrame), so it keeps moving even when the OS "reduce */
/*  motion" setting freezes CSS animations. Content sections stay clean. */
/* ------------------------------------------------------------------ */

const GREEN = "127,180,50";
const AMBER = "235,147,9";

const AboutHero = ({ eyebrow, title, crumbs = [] }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let raf = 0;
    let bubbles = [];

    // Reset a bubble. `initial` spreads them across the hero on first run;
    // otherwise it respawns just below the bottom edge to rise again.
    const reset = (b, initial) => {
      b.r = 6 + Math.random() * 16;
      b.x = Math.random() * w;
      b.y = initial ? Math.random() * h : h + b.r + Math.random() * 30;
      b.sp = 0.25 + Math.random() * 0.5; // rise speed (px/frame)
      b.vx = (Math.random() - 0.5) * 0.25; // gentle horizontal drift
      b.col = Math.random() > 0.5 ? GREEN : AMBER;
      return b;
    };

    const seed = () => {
      const count = Math.max(7, Math.min(16, Math.round(w / 110)));
      bubbles = [];
      for (let i = 0; i < count; i++) bubbles.push(reset({}, true));
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const frame = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        b.y -= b.sp;
        b.x += b.vx;
        if (b.y < -b.r - 4 || b.x < -b.r || b.x > w + b.r) {
          reset(b, false);
          continue;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${b.col},0.07)`;
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = `rgba(${b.col},0.3)`;
        ctx.stroke();
        // little highlight so it reads as a bubble
        ctx.beginPath();
        ctx.arc(b.x - b.r * 0.32, b.y - b.r * 0.32, Math.max(1, b.r * 0.16), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fill();
      }
      raf = requestAnimationFrame(frame);
    };

    resize();
    seed();
    frame();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section className="about-hero">
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <canvas className="about-hero__bg" ref={canvasRef} aria-hidden="true" />
      <div className="wrap">
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {crumbs.length > 0 && (
          <div className="crumb">
            {crumbs.map((c, i) => (
              <Fragment key={i}>
                {i > 0 && <span className="sep">/</span>}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span className="cur">{c.label}</span>}
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AboutHero;
